import React, { useState, Fragment } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const PrivacyPolicy = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (sectionTitle) => {
    setOpenSection(openSection === sectionTitle ? null : sectionTitle);
  };

  const renderExpandableSection = (title, content) => {
    const isOpen = openSection === title;
    return (
      <div className="rounded-[20px] sm:rounded-[24px] bg-white/[0.03] border border-white/[0.06] overflow-hidden transition-all duration-300 hover:border-white/[0.12]">
        <button
          onClick={() => toggleSection(title)}
          className="w-full flex items-center justify-between p-5 sm:p-6 md:p-7 text-left text-white font-medium text-base sm:text-lg cursor-pointer hover:bg-white/[0.02] transition-colors"
        >
          <span className="pr-4 leading-snug">{title}</span>
          <div className="w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center text-zinc-400 shrink-0">
            {isOpen ? <Minus className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4" />}
          </div>
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-5 sm:px-6 md:px-7 pb-6 text-sm sm:text-base text-zinc-300 font-light leading-relaxed border-t border-white/[0.04] pt-5 space-y-4">
                {content}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <>
      <div className="privacy-policy-container bg-black text-gray-300 min-h-screen py-8 font-sans">
        <div className="privacy-policy-content max-w-4xl mx-auto px-6 md:px-10">
          <h1 className="font-bold text-4xl md:text-5xl text-white mb-2 text-left border-b border-gray-800 pb-4 font-primary">
            Privacy Policy
          </h1>

          <div className="last-updated text-sm text-gray-500 text-left mt-2 mb-10 pt-0 border-t-0">
            Updated on 9th January, 2026
          </div>

          <section className="mb-10">
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-5 text-left">
              FOZZIL respects your privacy and is committed to protecting your
              personal data. This Privacy Policy explains how FOZZIL collects,
              uses, and shares information when you use our services or interact
              with our website.
            </p>
          </section>

          <img
            src="/assets/varaen-logo.jpg"
            alt="FOZZIL VARAEN Security"
            className="privacy-policy-image block mx-auto my-8 max-w-[200px] w-full h-auto rounded-3xl border border-[#065DC0]/40 shadow-[0_0_40px_rgba(6,93,192,0.35)] object-cover"
          />
          <p className="text-center text-xs font-medium text-[#065DC0] font-mono tracking-wider mb-10 -mt-4">
            Protected by FOZZIL VARAEN · S.H.A.D.E. Defense Engine
          </p>
          <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-5 text-left">
            Before using these features, you'll be given the opportunity to
            review this product-specific information. You can also view this
            information at any time in the relevant settings or by visiting our
            website at /privacy.
          </p>

          <section className="privacy-policy-contents mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-6 text-left">
              Contents
            </h2>
            <ul className="list-disc ml-8 mb-5 text-gray-400">
              <li>Data & Privacy Information</li>
              <li>Your Privacy Rights</li>
              <li>Product-Specific Privacy Information</li>
              <li>FOZZIL ID & Authentication</li>
              <li>FOZZIL Store</li>
              <li>FOZZIL ARK & Spatial Data</li>
              <li>FOZZIL MEDOS & Healthcare Data</li>
              <li>FOZZIL Ecosystem & Cross-Platform Data</li>
              <li>Cookies & Tracking Technologies</li>
              <li>Data Security</li>
              <li>Privacy Policy Updates</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 text-left">
              Data & Privacy Information
            </h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              We collect and use personal data to improve your experience with
              FOZZIL's services and products. Depending on the features you use,
              you may be asked to provide personal information, such as your
              name, email address, or IP address. FOZZIL ensures that your data
              is used only as specified in this policy and within
              product-specific guidelines.
            </p>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              You can view our product-specific privacy practices in your
              settings or at any time by visiting /privacy.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 text-left">
              Your Privacy Rights
            </h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              FOZZIL acknowledges your rights to:
            </p>
            <ul className="list-disc ml-8 mb-3 text-gray-400">
              <li>Access, correct, or delete your personal data.</li>
              <li>Limit or object to how your personal data is processed.</li>
              <li>Withdraw consent at any time.</li>
            </ul>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              If you have any concerns or requests about your personal data,
              please contact us at privacy@fozzil.in.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 text-left">
              Product-Specific Privacy Information
            </h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              Certain FOZZIL products and services come with specific data and
              privacy guidelines. These guidelines are designed to give you
              transparency and control over how your data is collected and used.
            </p>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              Examples of product-specific data usage include:
            </p>
            <ul className="list-disc ml-8 mb-3 text-gray-400">
              <li>
                <strong>T.A.E.L.S. (Truly Adaptive, Efficient & Learning Synthesis):</strong> User input data (text, voice, and file uploads) is processed through isolated, secure inference channels to deliver real-time assistance and adaptive cognitive context without persistent plaintext retention or unauthorized model training.
              </li>
              <li>
                ZYPHER AI: Personal health data may be processed for clinical
                advisory, risk prediction, and analytics purposes but will always require your explicit
                consent.
              </li>
              <li>
                FOZZIL ARK: Spatial awareness data, visitor interaction patterns, and audience engagement
                metrics may be collected via the S.H.A.D.E. Vision system to enable responsive exhibit
                experiences. No personally identifiable biometric data is stored permanently.
              </li>
              <li>
                FOZZIL MEDOS: Operational data including organisation name, billing records,
                inventory information, and staff account details are collected to deliver healthcare
                management services.
              </li>
              <li>
                FOZZIL Store: Order history, shipping addresses, saved items, and browsing preferences
                are collected to facilitate e-commerce transactions and personalise your shopping experience.
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 text-left">
              FOZZIL ID & Authentication
            </h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              FOZZIL ID is your unified identity across the entire FOZZIL ecosystem.
              When you create a FOZZIL ID, we collect your name, email address, and
              authentication credentials. FOZZIL supports modern passkey-based
              authentication, which uses your device's biometric or screen-lock
              verification rather than traditional passwords, enhancing security while
              protecting your privacy.
            </p>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              Your FOZZIL ID enables single sign-on (SSO) across all FOZZIL services
              and subdomains — including fozzil.in, taels.fozzil.in,
              account.fozzil.in, store.fozzil.in, and ecosystem.fozzil.in. Session
              authentication tokens are shared across these domains to provide a
              seamless experience.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 text-left">
              FOZZIL Ecosystem & Cross-Platform Data
            </h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              The FOZZIL Ecosystem connects multiple FOZZIL services into a unified platform.
              When you use the Ecosystem, data such as your subscription status, device
              registrations, and service preferences may be synchronised across platforms
              to provide an integrated experience.
            </p>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              You can manage your Ecosystem data and connected services through your
              FOZZIL Account settings at account.fozzil.in.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 text-left">
              Data Security
            </h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              FOZZIL uses state-of-the-art encryption and security practices to
              protect your personal data. However, no method of electronic
              transmission is 100% secure, and we cannot guarantee the absolute
              security of your information.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold text-white mb-4 text-left">
              Privacy Policy Updates
            </h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-3 text-left">
              FOZZIL may occasionally update this policy to reflect changes in
              technology or legal requirements. We encourage you to review this
              page periodically for any updates. Your continued use of our
              services after changes to the Privacy Policy implies acceptance of
              those changes.
            </p>
          </section>

          {/* ========================================================================= */}
          {/* ANSWERS & SPECIFICATIONS · FREQUENTLY ASKED QUESTIONS                     */}
          {/* ========================================================================= */}
          <section className="pt-16 pb-8 border-t border-white/[0.08] mt-12">
            <div className="space-y-3 mb-10 text-center sm:text-left">
              <span className="text-xs font-mono text-blue-400 uppercase tracking-[0.25em]">
                Answers & Specifications
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-white tracking-tight">
                Frequently Asked Questions
              </h2>
              <p className="text-sm sm:text-base text-zinc-400 font-light leading-relaxed">
                Detailed definitions, rights, processing activities, and global safeguards.
              </p>
            </div>

            <div className="space-y-4">
              {renderExpandableSection(
            "What Is Personal Data at FOZZIL?",
            <Fragment>
              <p>
                At FOZZIL, we firmly believe in your right to privacy, no matter
                where you live in the world. We define personal data as any data
                related to an identified or identifiable individual or any data
                that can be linked to them directly or indirectly by FOZZIL.
                This includes data such as your name or data that does not
                directly identify you but can be used to identify you, such as
                your device's serial number. Aggregated data is considered
                non-personal data for the purposes of this Privacy Policy.
              </p>
              <p>
                This Privacy Policy applies to how FOZZIL handles your personal
                data, whether you interact with us on our website, through
                FOZZIL apps, or in person. FOZZIL may also provide links to
                third-party apps or services; our Privacy Policy does not apply
                to third parties, and we encourage you to review their privacy
                practices before interacting with them.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Your Privacy Rights at FOZZIL",
            <Fragment>
              <p>
                At FOZZIL, we respect your rights regarding your personal data.
                These include the rights to access, correct, restrict the
                processing of, transfer, or delete your personal data. We
                provide these rights to our global customers, and if you choose
                to exercise them, you have the right to do so without facing
                discrimination from FOZZIL.
              </p>
              <p>
                To exercise your privacy rights, visit the FOZZIL Data & Privacy
                page at /privacy. For the protection of your personal
                data, your identity may need to be verified before any request
                is processed. In some cases, we may not be able to fulfill your
                request — for example, if retaining certain data is necessary
                for legal obligations or fraud prevention purposes.
              </p>
              <p>
                If you live in certain regions and cannot access the FOZZIL Data
                & Privacy page, you or your authorized representative can submit
                a request through <a href="https://support.fozzil.in/contact" className="text-blue-400 hover:underline">support.fozzil.in/contact</a>.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Personal Data FOZZIL Collects from You",
            <Fragment>
              <p>
                FOZZIL is committed to collecting only the personal data that is
                necessary for providing great products and services. The type of
                personal data FOZZIL collects depends on how you interact with
                us. When you create an account, contact customer support, or
                interact with our services, we may collect various types of
                information, including:
              </p>
              <ul>
                <li>
                  Device Information: Information such as device serial numbers
                  and browser types that can identify your device.
                </li>
                <li>
                  Contact Information: Your name, email, phone number, and
                  physical address.
                </li>
                <li>
                  Payment Information: Billing information including payment
                  methods (e.g., bank details, credit/debit card details).
                </li>
                <li>
                  Transaction Information: Data about purchases made through
                  FOZZIL platforms and services.
                </li>
                <li>
                  Usage Data: Data related to your usage of FOZZIL services such
                  as browsing history, product interactions, and diagnostics.
                </li>
                <li>
                  Location Information: Precise or coarse location data for
                  region-specific services or features.
                </li>
                <li>
                  Fraud Prevention Information: Data used to identify and
                  prevent fraud.
                </li>
                <li>
                  Health & Fitness Information: Personal health data, including
                  fitness details shared with FOZZIL services.
                </li>
                <li>
                  Government ID Data: In some regions, we may collect
                  government-issued IDs to set up accounts or as required by
                  law.
                </li>
                <li className="text-gray-400">
                  Other Information You Provide: This includes any content or
                  information you share during interactions with customer
                  support or through social media.
                </li>
              </ul>
              <h3 className="font-semibold text-lg mt-4 mb-2 text-white">Payment Processing & Security</h3>
              <p className="mb-3 text-gray-300">
                To provide FOZZIL Pay services, we utilize authorized, RBI-licensed payment aggregation partners (such as PayU) to process transactions. While your experience is branded as FOZZIL Pay, the underlying technical processing and authorization are handled by our secure partners.
              </p>
              <ul className="list-disc ml-8 mb-3 text-gray-400">
                <li className="text-gray-400">
                  <strong className="text-gray-200">Transaction Data:</strong> When you use FOZZIL Pay, we collect transaction-related information required to process your payment. This includes your name, email address, mobile number, and payment instrument details (such as UPI ID or masked card tokens).
                </li>
                <li className="text-gray-400">
                  <strong className="text-gray-200">No Storage of Full Credentials:</strong> We do not store your full card credentials on our own servers. All sensitive payment data is encrypted with industry-standard cryptographic protocols and processed in environments compliant with PCI-DSS (Payment Card Industry Data Security Standard).
                </li>
                <li className="text-gray-400">
                  <strong className="text-gray-200">Data Localization:</strong> In compliance with Reserve Bank of India (RBI) guidelines, all payment-related data is stored and processed exclusively on secure servers located within the territory of India.
                </li>
              </ul>
              <p>
                If you choose not to provide certain personal data, FOZZIL may
                be unable to offer some products or services or respond to your
                requests.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Personal Data FOZZIL Receives from Other Sources",
            <Fragment>
              <p>Individuals:</p>
              <p>
                FOZZIL may collect data about you from other individuals if they
                send you products, invites, or share content with you related to
                FOZZIL services.
              </p>
              <p>At Your Direction:</p>
              <p>
                You may direct third parties to share data with FOZZIL, such as
                when using third-party services integrated with FOZZIL's
                products.
              </p>
              <p>FOZZIL Partners:</p>
              <p>
                FOZZIL may validate information with partners for security and
                fraud-prevention purposes.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "FOZZIL's Use of Personal Data",
            <Fragment>
              <p>Power Our Services:</p>
              <p>
                FOZZIL collects necessary data to improve services, perform
                internal audits, and troubleshoot issues.
              </p>
              <p>Process Your Transactions:</p>
              <p>
                To complete transactions, FOZZIL collects details like your name
                and payment information.
              </p>
              <p>Communicate with You:</p>
              <p>
                FOZZIL may contact you regarding your account, transactions, or
                important notices.
              </p>
              <p>Security and Fraud Prevention:</p>
              <p>
                FOZZIL protects against fraud and scans uploaded content for
                illegal material.
              </p>
              <p>Personalization:</p>
              <p>
                If you opt for personalization, FOZZIL will use collected data
                to enhance your experience.
              </p>
              <p>Comply with Law:</p>
              <p>FOZZIL may process your data as required by applicable law.</p>
            </Fragment>
          )}

          {renderExpandableSection(
            "FOZZIL's Sharing of Personal Data",
            <Fragment>
              <p>Service Providers:</p>
              <p>
                FOZZIL may engage third parties to act on its behalf, such as
                processing data and delivering products. These providers must
                handle data in compliance with FOZZIL's policies.
              </p>
              <p>Partners:</p>
              <p>
                FOZZIL partners with third parties for services or financial
                offerings, requiring partners to protect your data.
              </p>
              <p>Others:</p>
              <p>
                FOZZIL may share data if legally required or necessary for
                security or operational protection.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Protection of Personal Data at FOZZIL",
            <Fragment>
              <p>
                FOZZIL uses robust administrative, technical, and physical
                safeguards to secure personal data and continuously improves
                these measures.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Children and Personal Data",
            <Fragment>
              <p>
                FOZZIL takes extra care to protect children's data, with special
                provisions for accounts managed by parents or educational
                institutions.
              </p>
            </Fragment>
          )}

          {renderExpandableSection(
            "Cookies and Other Technologies",
            <Fragment>
              <p>
                FOZZIL's website, online services, and interactive applications
                may use cookies and other technologies like web beacons. These
                technologies help us better understand user behavior, including
                for security and fraud prevention purposes, indicate which
                sections of our website users visit, and facilitate and measure
                the effectiveness of advertisements and web searches.
              </p>
              <p>Communications Cookies:</p>
              <p>
                These cookies allow network traffic between FOZZIL's systems and
                your device. They also help us detect any errors.
              </p>
              <p>Strictly Necessary Cookies:</p>
              <p>
                These cookies are essential to providing specific features or
                services you have requested. For instance, they enable us to
                display the website in the proper format, authenticate your
                transactions, and preserve the contents of your shopping cart.
              </p>
              <p>Other Cookies:</p>
              <p>
                These cookies help us understand how visitors interact with our
                website and online services. FOZZIL uses these cookies to assess
                the effectiveness of advertisements and web searches, as well as
                to remember your preferences, providing a customized experience.
              </p>
              <p>
                If you prefer that FOZZIL not use cookies, we provide options to
                disable them. If you are using a web browser, you can modify
                your browser settings to block all cookies. Please note, certain
                features of the FOZZIL website may not be available if you
                disable cookies.
              </p>
              <p>
                In addition to cookies, FOZZIL may use other technologies to
                achieve similar objectives, such as tracking URLs in emails to
                monitor interest in particular content. If you prefer not to be
                tracked in this way, you should avoid clicking graphic or text
                links in email messages.
              </p>
              <p>
                FOZZIL treats data collected via cookies and similar
                technologies as non-personal data. However, in jurisdictions
                where IP addresses or similar identifiers are considered
                personal data, we treat them as such. We may combine data
                collected from cookies with other personal data FOZZIL holds,
                treating the combined data as personal.
              </p>
              <p>
                FOZZIL does not track users or collect data for advertising
                purposes through third-party platforms or link personal
                information across other companies.
              </p>
              <p>
                If you do not want to receive ads targeted to your interests
                from FOZZIL's advertising platforms, you can disable
                personalized ads through your device settings. You may still see
                ads based on the context of your activity on our website.
              </p>
            </Fragment>
          )}


          {renderExpandableSection(
            "Transfer of Personal Data Between Countries",
            <Fragment>
              <p>
                FOZZIL is committed to connecting users worldwide with our
                products and services. This may involve transferring your
                personal data to or accessing it from entities around the globe,
                including FOZZIL-affiliated companies. We comply with relevant
                data protection laws to ensure that your data is protected no
                matter where it is processed.
              </p>
              <p>
                The control of your personal data may vary based on your
                location. For example, personal data related to FOZZIL's
                e-commerce services may be controlled by different entities in
                your region. Regardless of where your data is processed or
                stored, FOZZIL maintains the same high standards of protection.
              </p>
              <p>
                For personal data originating in regions like the European
                Economic Area (EEA) or the United Kingdom, FOZZIL ensures that
                cross-border transfers comply with applicable regulations and
                are governed by Standard Contractual Clauses or other legal
                mechanisms.
              </p>
              <p>
                If you have questions about how FOZZIL processes your data or
                wish to understand more about international data transfers, you
                can contact us through our support channels.
              </p>
            </Fragment>
          )}
          </div>
        </section>

          <section className="privacy-policy-contact border-t border-gray-800 pt-8 mt-12">
            <h2 className="text-xl font-bold text-white mb-4">Contact Us</h2>
            <p className="text-gray-400">
              If you have any questions or concerns about this Privacy Policy,
              please contact us at: <br />
              <span className="text-gray-300">FOZZIL</span><br />
              Chennai- 603203 Tamil Nadu, India<br />
              <a href="mailto:privacy@fozzil.in" className="text-blue-400 hover:underline">privacy@fozzil.in</a>
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
